import React, { memo } from 'react';
import { FormControl, FormHelperText, InputLabel, Select } from '@material-ui/core';
import { CustomFormControlProps, CustomSelectType, CustomSelectTypeOptions } from '../../../model/global';
import { CustomFormFieldState } from '../CustomFormField/CustomFormField';
import { useSelector } from 'react-redux';
import { translationsSelector } from '../../../store/modules/translations/selectors';
import { getPrefixedFieldId } from '../../utils';
import useStyles from './styles';

const SelectBox: React.FC<CustomFormControlProps> = ({ field, onChange, fullWidth, validationError }): JSX.Element => {

	const classes = useStyles();
	const { formRequiredField } = useSelector(translationsSelector)
	const [validationMessageState, setValidationMessageState] = React.useState('');

	const handleChange = (event: React.ChangeEvent<any>) => {

		const { name, value, required, validity: { valid } } = event.target as HTMLSelectElement;
		if (!valid) setValidationMessageState(formRequiredField || 'This field is required');
		else setValidationMessageState('');

		const change: CustomFormFieldState = {
			name,
			value,
			required,
			valid,
			invalidReason: field.invalidReason || '',
			originalField: field.originalField,
		};

		// support for having options which are not the original field options!
		if (field.options) change['options'] = field.options
		onChange(change)
	};

	const renderOption = (option: CustomSelectType['options'][0], key: number): JSX.Element => {
		return <option key={key} value={option.value}>{option.label}</option>
	}
	const renderGroup = (group: CustomSelectTypeOptions, key: number): JSX.Element => {
		return (
			<optgroup label={group.label} key={`${group.value}_${key}`}>
				{group.options ? group.options.map((option, idx) => renderOption(option, idx)) : null}
			</optgroup>
		)
	}

	/** Support for external field validation messages */
	const getHelperText = (): string => validationError || validationMessageState || '';

	const renderControlOptions = (field: CustomFormFieldState): JSX.Element => {

		const selectControl = field.originalField as CustomSelectType;
		const hasCustomOptions = field.options && field.options.length > 0
		const isGroup = selectControl.optionsGroup && selectControl.optionsGroup.length > 0;
		const hasOriginalOptions = selectControl.options && selectControl.options.length > 0

		switch (true) {
			case isGroup: {
				return (field.originalField as CustomSelectType).optionsGroup && (field.originalField as any).optionsGroup.map((group, idx) => {
					{ return renderGroup(group, idx) }
				})
			}
			case hasCustomOptions: {
				return (field.options as any) && (field.options as any).map((option, idx) => {
					{ return renderOption(option, idx) }
				})
			}
			case hasOriginalOptions: {
				return (selectControl.options as any) && (selectControl.options as any).map((option, idx) => {
					{ return renderOption(option, idx) }
				})
			}
			default: break;
		}
		return <></>
	}

	// setup an ID for the label htmlFor
	const controlId = getPrefixedFieldId(field.originalField.id || field.originalField.name);
	const controlClasses: string[] = [classes.formControl];

	return (
		<FormControl className={controlClasses.join(' ')} required={!!field.required} fullWidth={!!fullWidth}>
			<InputLabel className={classes.label} htmlFor={controlId} shrink={true}>{field.originalField.title}</InputLabel>
			<Select
				native
				value={field.value}
				className={classes.select}
				fullWidth={fullWidth}
				name={field.name}
				id={controlId}
				onChange={e => handleChange(e)}
				inputProps={{
					name: field.name,
					id: controlId,
				}}>
				{
					renderControlOptions(field)
				}
			</Select>
			<FormHelperText className={classes.validationWrapper}>{getHelperText()}</FormHelperText>
		</FormControl>
	)
};

export default memo(SelectBox);
